// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
    baseUrl: `${string}://${string}` | (string & {});
};

/**
 * AdminAnswerFirstRecipientSchema
 *
 * A user on the AnswerFirst failure-notification list.
 *
 * ``name`` and ``email`` come off the linked user, so they follow any later
 * edit to that account.
 */
export type AdminAnswerFirstRecipientSchema = {
    /**
     * Id
     */
    id: string;
    /**
     * User Id
     */
    user_id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Email
     */
    email: string;
    /**
     * Created At
     */
    created_at: string;
};

/**
 * AdminCompanySchema
 *
 * A company as the admin list shows it.
 */
export type AdminCompanySchema = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * City
     */
    city: string;
    /**
     * State
     */
    state: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Last Updated At
     */
    last_updated_at?: string | null;
};

/**
 * AdminCreateAnswerFirstRecipient
 *
 * Request body for adding a recipient.
 */
export type AdminCreateAnswerFirstRecipient = {
    /**
     * User Id
     */
    user_id: string;
};

/**
 * AdminFacilityCompanySchema
 *
 * The owning company, nested on a facility row.
 */
export type AdminFacilityCompanySchema = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
};

/**
 * AdminFacilitySchema
 *
 * A facility as the admin list shows it.
 */
export type AdminFacilitySchema = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * City
     */
    city: string;
    /**
     * State
     */
    state: string;
    /**
     * Zip Code
     */
    zip_code: string;
    /**
     * Timezone
     */
    timezone: string;
    company?: AdminFacilityCompanySchema | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Last Updated At
     */
    last_updated_at?: string | null;
};

/**
 * AdminPaginatedCompanies
 *
 * Paginated company list for the admin app.
 */
export type AdminPaginatedCompanies = {
    /**
     * Items
     */
    items: Array<AdminCompanySchema>;
    /**
     * Total
     */
    total: number;
};

/**
 * AdminPaginatedFacilities
 *
 * Paginated facility list for the admin app.
 */
export type AdminPaginatedFacilities = {
    /**
     * Items
     */
    items: Array<AdminFacilitySchema>;
    /**
     * Total
     */
    total: number;
};

/**
 * AdminPaginatedUsers
 *
 * Paginated user list for the admin app.
 */
export type AdminPaginatedUsers = {
    /**
     * Items
     */
    items: Array<AdminUserSchema>;
    /**
     * Total
     */
    total: number;
};

/**
 * AdminUserOption
 *
 * A Luna user as a picker option — just enough to render and identify.
 */
export type AdminUserOption = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Email
     */
    email: string;
    role?: UserRoles | null;
};

/**
 * AdminUserSchema
 *
 * A user as the admin list shows it.
 */
export type AdminUserSchema = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Email
     */
    email: string;
    role?: UserRoles | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Last Updated At
     */
    last_updated_at?: string | null;
};

/**
 * AppUserPermifyPermissions
 */
export type AppUserPermifyPermissions = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Email
     */
    email: string;
    /**
     * Role
     */
    role: string | null;
    /**
     * Permify Permissions
     *
     * Platform-level relations granted to the user
     */
    permify_permissions?: Array<string>;
    /**
     * Expected Permission
     *
     * Platform relation implied by the user's DB role
     */
    expected_permission?: string | null;
    /**
     * In Sync
     *
     * Whether expected_permission is present in Permify
     */
    in_sync: boolean;
};

/**
 * AutoRecipientRole
 */
export const AutoRecipientRole = {
    ACCOUNT_MANAGER: 'Account Manager',
    REGION_ADMIN: 'Region Admin',
    ADMIN: 'Admin'
} as const;

/**
 * AutoRecipientRole
 */
export type AutoRecipientRole = typeof AutoRecipientRole[keyof typeof AutoRecipientRole];

/**
 * CheckPermissionRequestBody
 */
export type CheckPermissionRequestBody = {
    /**
     * Entity Type
     */
    entity_type: string;
    /**
     * Entity Id
     */
    entity_id: string;
    /**
     * Permission
     */
    permission: string;
    /**
     * Subject Type
     */
    subject_type: string;
    /**
     * Subject Id
     */
    subject_id: string;
    /**
     * Schema Version
     */
    schema_version?: string | null;
};

/**
 * CheckPermissionResponseBody
 */
export type CheckPermissionResponseBody = {
    /**
     * Allowed
     */
    allowed: boolean;
    /**
     * Metadata
     */
    metadata: {
        [key: string]: unknown;
    };
};

/**
 * Child
 */
export type Child = {
    leaf?: Leaf | null;
    rewrite?: Rewrite | null;
};

/**
 * ClinicDaySchema
 *
 * Schema for clinic day information.
 */
export type ClinicDaySchema = {
    /**
     * Id
     */
    id: string;
    /**
     * Day Of Week
     */
    day_of_week: string;
    /**
     * Start Time
     */
    start_time: string;
    /**
     * End Time
     */
    end_time: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Last Updated At
     */
    last_updated_at?: string | null;
    /**
     * Deleted At
     */
    deleted_at?: string | null;
};

/**
 * ComputedRelation
 */
export type ComputedRelation = {
    /**
     * Relation
     */
    relation: string;
};

/**
 * ComputedUserSet
 */
export type ComputedUserSet = {
    /**
     * Relation
     */
    relation: string;
};

/**
 * CoverageEncounterVolumeRow
 *
 * Encounters (patients seen) completed at one facility, of one type, in one week.
 */
export type CoverageEncounterVolumeRow = {
    /**
     * Facility Id
     */
    facility_id: string;
    encounter_type: EncounterTypes;
    /**
     * Week Start
     */
    week_start: string;
    /**
     * Count
     */
    count: number;
};

/**
 * CoverageFacility
 *
 * A geocoded facility pin on the coverage map.
 */
export type CoverageFacility = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * City
     */
    city: string;
    /**
     * State
     */
    state: string;
    /**
     * Lat
     */
    lat: number;
    /**
     * Long
     */
    long: number;
    /**
     * Region Id
     */
    region_id: string;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Company Name
     */
    company_name?: string | null;
    /**
     * On Hold
     */
    on_hold: boolean;
    /**
     * Last Job At
     */
    last_job_at?: string | null;
};

/**
 * CoverageJobVolumeRow
 *
 * Jobs one nurse visited at one facility, of one type, in one week.
 */
export type CoverageJobVolumeRow = {
    /**
     * Facility Id
     */
    facility_id: string;
    job_type: JobTypes;
    /**
     * Week Start
     */
    week_start: string;
    /**
     * Nurse Id
     */
    nurse_id?: string | null;
    /**
     * Count
     */
    count: number;
};

/**
 * CoverageMap
 *
 * Everything the coverage map plots, plus what it had to leave off.
 */
export type CoverageMap = {
    /**
     * Regions
     */
    regions: Array<CoverageRegion>;
    /**
     * Facilities
     */
    facilities: Array<CoverageFacility>;
    /**
     * Nurses
     */
    nurses: Array<CoverageNurse>;
    /**
     * Unlocated Nurses
     */
    unlocated_nurses: Array<CoverageUnlocatedNurse>;
    /**
     * Facilities Missing Location
     */
    facilities_missing_location: number;
    /**
     * Nurses Missing Location
     */
    nurses_missing_location: number;
};

/**
 * CoverageNurse
 *
 * A geocoded nurse home base on the coverage map.
 */
export type CoverageNurse = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    role: UserRoles;
    /**
     * Lat
     */
    lat: number;
    /**
     * Long
     */
    long: number;
    /**
     * Region Ids
     */
    region_ids: Array<string>;
    /**
     * Capabilities
     */
    capabilities: Array<ProcedureType>;
};

/**
 * CoverageProspect
 *
 * A CMS-certified nursing home from the latest provider release.
 */
export type CoverageProspect = {
    /**
     * Ccn
     */
    ccn: string;
    /**
     * Name
     */
    name: string;
    /**
     * City
     */
    city?: string | null;
    /**
     * State
     */
    state?: string | null;
    /**
     * Lat
     */
    lat: number;
    /**
     * Long
     */
    long: number;
    /**
     * Beds
     */
    beds?: number | null;
    /**
     * Average Residents
     */
    average_residents?: number | null;
    /**
     * Overall Rating
     */
    overall_rating?: number | null;
    /**
     * Chain Name
     */
    chain_name?: string | null;
    /**
     * Facility Id
     */
    facility_id?: string | null;
};

/**
 * CoverageProspects
 *
 * Every located nursing home in the latest CMS release.
 */
export type CoverageProspects = {
    /**
     * Processing Date
     */
    processing_date?: string | null;
    /**
     * Prospects
     */
    prospects: Array<CoverageProspect>;
};

/**
 * CoverageRegion
 *
 * A region, for the coverage map's filters and grouping.
 */
export type CoverageRegion = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
};

/**
 * CoverageUnlocatedNurse
 *
 * A nurse the map can't place because their home address isn't geocoded.
 */
export type CoverageUnlocatedNurse = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    role: UserRoles;
};

/**
 * CoverageVolume
 *
 * Weekly job and encounter counts per facility since ``since``.
 *
 * Weeks start Monday, UTC. Jobs are bucketed by when they were visited,
 * encounters by completion (the service date).
 */
export type CoverageVolume = {
    /**
     * Since
     */
    since: string;
    /**
     * Jobs
     */
    jobs: Array<CoverageJobVolumeRow>;
    /**
     * Encounters
     */
    encounters: Array<CoverageEncounterVolumeRow>;
};

/**
 * CreateSchemaRequest
 */
export type CreateSchemaRequest = {
    /**
     * Schema Text
     *
     * Permify schema DSL source
     */
    schema_text: string;
};

/**
 * CreateTenantRequest
 */
export type CreateTenantRequest = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
};

/**
 * DashboardSubscriptionSchema
 */
export type DashboardSubscriptionSchema = {
    /**
     * Id
     */
    id: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at?: string | null;
    /**
     * Deleted At
     */
    deleted_at?: string | null;
    /**
     * To Emails
     */
    to_emails: Array<string>;
    typ: SubscriptionType;
    /**
     * Send Delay Days
     */
    send_delay_days: number;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Facility Id
     */
    facility_id?: string | null;
    /**
     * Metabase Report Id
     */
    metabase_report_id: string;
    /**
     * Send Hour
     */
    send_hour: number;
};

/**
 * DispatchBoard
 *
 * Today's infusion and vascular access jobs, and each nurse's route through them.
 */
export type DispatchBoard = {
    /**
     * Generated At
     */
    generated_at: string;
    /**
     * Late Grace Minutes
     */
    late_grace_minutes: number;
    /**
     * Regions
     */
    regions: Array<CoverageRegion>;
    /**
     * Facilities
     */
    facilities: Array<DispatchFacility>;
    /**
     * Jobs
     */
    jobs: Array<DispatchJob>;
    /**
     * Nurses
     */
    nurses: Array<DispatchNurse>;
    /**
     * Idle Nurses
     */
    idle_nurses: Array<DispatchIdleNurse>;
};

/**
 * DispatchFacility
 *
 * A facility with a job on today's board; it can't be plotted until it's geocoded.
 */
export type DispatchFacility = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * City
     */
    city: string;
    /**
     * State
     */
    state: string;
    /**
     * Timezone
     */
    timezone: string;
    /**
     * Region Id
     */
    region_id: string;
    /**
     * Lat
     */
    lat?: number | null;
    /**
     * Long
     */
    long?: number | null;
};

/**
 * DispatchIdleNurse
 *
 * A field nurse in the selected regions with nothing on today's board, shown at home.
 */
export type DispatchIdleNurse = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Lat
     */
    lat: number;
    /**
     * Long
     */
    long: number;
};

/**
 * DispatchJob
 *
 * An infusion or vascular access job on today's board.
 */
export type DispatchJob = {
    /**
     * Id
     */
    id: string;
    job_type: JobTypes;
    status: JobStatuses;
    /**
     * Facility Id
     */
    facility_id: string;
    /**
     * Nurse Id
     */
    nurse_id?: string | null;
    /**
     * Requested For
     */
    requested_for?: string | null;
    /**
     * Nurse Eta
     */
    nurse_eta?: string | null;
    /**
     * Due At
     */
    due_at?: string | null;
    /**
     * Check In At
     */
    check_in_at?: string | null;
    /**
     * Check Out At
     */
    check_out_at?: string | null;
    /**
     * Late
     */
    late: boolean;
    /**
     * Carried Over
     */
    carried_over: boolean;
    /**
     * Earliest Arrival
     */
    earliest_arrival?: string | null;
    /**
     * At Risk
     */
    at_risk: boolean;
};

/**
 * DispatchLeg
 *
 * The drive into one stop of a nurse's queue.
 */
export type DispatchLeg = {
    /**
     * Job Id
     */
    job_id: string;
    /**
     * From Facility Id
     */
    from_facility_id?: string | null;
    /**
     * To Facility Id
     */
    to_facility_id: string;
    /**
     * State
     */
    state: 'traveled' | 'next' | 'planned';
    /**
     * Drive Seconds
     */
    drive_seconds?: number | null;
    /**
     * Distance Miles
     */
    distance_miles?: number | null;
    /**
     * Has Road
     */
    has_road?: boolean;
};

/**
 * DispatchNurse
 *
 * A nurse with jobs today: their queue in visiting order and where they're inferred to be.
 */
export type DispatchNurse = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Home Lat
     */
    home_lat?: number | null;
    /**
     * Home Long
     */
    home_long?: number | null;
    /**
     * State
     */
    state: 'not_started' | 'on_site' | 'between_stops' | 'done';
    /**
     * Facility Id
     */
    facility_id?: string | null;
    /**
     * Since
     */
    since?: string | null;
    /**
     * Job Ids
     */
    job_ids: Array<string>;
    /**
     * Legs
     */
    legs: Array<DispatchLeg>;
};

/**
 * DispatchNurseRoute
 *
 * The roads one nurse drives today; legs whose road isn't cached yet are left out.
 */
export type DispatchNurseRoute = {
    /**
     * Nurse Id
     */
    nurse_id: string;
    /**
     * Legs
     */
    legs: Array<DispatchRoadLeg>;
};

/**
 * DispatchRoadLeg
 *
 * One leg of a nurse's day along the actual roads.
 */
export type DispatchRoadLeg = {
    /**
     * Job Id
     */
    job_id: string;
    /**
     * From Facility Id
     */
    from_facility_id?: string | null;
    /**
     * To Facility Id
     */
    to_facility_id: string;
    /**
     * Points
     */
    points: Array<Array<number>>;
};

/**
 * EncounterTypes
 */
export const EncounterTypes = {
    INFUSION: 'Infusion',
    VASCULAR_ACCESS: 'Vascular Access',
    BLOOD_TRANSFUSION_QUALIFICATION_CALL: 'Blood Transfusion Qualification Call',
    BLOOD_TRANSFUSION_TYPE_AND_SCREEN: 'Blood Transfusion Type and Screen',
    BLOOD_TRANSFUSION_TYPE_AND_SCREEN_DROP_OFF: 'Blood Transfusion Type and Screen Drop Off',
    BLOOD_TRANSFUSION_UNIT_PICKUP: 'Blood Transfusion Unit Pickup',
    BLOOD_TRANSFUSION_ADMINISTRATION: 'Blood Transfusion Administration',
    BLOOD_TRANSFUSION_24_HOUR_FOLLOW_UP_CALL: 'Blood Transfusion 24 Hour Follow Up Call',
    BLOOD_TRANSFUSION_30_DAY_FOLLOW_UP_CALL: 'Blood Transfusion 30 Day Follow Up Call'
} as const;

/**
 * EncounterTypes
 */
export type EncounterTypes = typeof EncounterTypes[keyof typeof EncounterTypes];

/**
 * EntityDefinition
 */
export type EntityDefinition = {
    /**
     * Name
     */
    name: string;
    /**
     * Relations
     */
    relations?: {
        [key: string]: RelationDefinition;
    };
    /**
     * Permissions
     */
    permissions?: {
        [key: string]: PermissionDefinition;
    };
    /**
     * Attributes
     */
    attributes?: {
        [key: string]: unknown;
    };
    /**
     * References
     */
    references?: {
        [key: string]: EntityReferenceKind;
    };
};

/**
 * EntityFilterBody
 */
export type EntityFilterBody = {
    /**
     * Type
     */
    type?: string | null;
    /**
     * Ids
     */
    ids?: Array<string> | null;
};

/**
 * EntityReferenceKind
 */
export const EntityReferenceKind = {
    REFERENCE_UNSPECIFIED: 'REFERENCE_UNSPECIFIED',
    REFERENCE_RELATION: 'REFERENCE_RELATION',
    REFERENCE_PERMISSION: 'REFERENCE_PERMISSION',
    REFERENCE_ATTRIBUTE: 'REFERENCE_ATTRIBUTE'
} as const;

/**
 * EntityReferenceKind
 */
export type EntityReferenceKind = typeof EntityReferenceKind[keyof typeof EntityReferenceKind];

/**
 * EntitySummary
 */
export type EntitySummary = {
    /**
     * Name
     */
    name: string;
    /**
     * Relations
     */
    relations: Array<RelationSummary>;
    /**
     * Permissions
     */
    permissions: Array<PermissionSummary>;
};

/**
 * ErrorDetail
 *
 * The body FastAPI produces for a raised ``HTTPException``.
 */
export type ErrorDetail = {
    /**
     * Detail
     */
    detail: string;
};

/**
 * ExpectedPermifyTenant
 */
export type ExpectedPermifyTenant = {
    /**
     * Id
     *
     * Permify tenant id, e.g. customer-platform-local
     */
    id: string;
    /**
     * App
     *
     * Application key: customer-platform or luna
     */
    app: string;
    /**
     * Name
     *
     * Human-readable tenant name
     */
    name: string;
    /**
     * Exists
     *
     * Whether the tenant is registered in Permify
     */
    exists: boolean;
    /**
     * Schema Version
     *
     * Latest schema version, if the tenant exists and has a schema
     */
    schema_version?: string | null;
};

/**
 * FeatureFlagCreateRequest
 */
export type FeatureFlagCreateRequest = {
    /**
     * Key
     */
    key: string;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Enabled
     */
    enabled?: boolean;
    /**
     * Everyone
     */
    everyone?: boolean;
    /**
     * Roles
     */
    roles?: Array<string>;
    /**
     * User Ids
     */
    user_ids?: Array<string>;
};

/**
 * FeatureFlagSchema
 */
export type FeatureFlagSchema = {
    /**
     * Id
     */
    id: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at?: string | null;
    /**
     * Deleted At
     */
    deleted_at?: string | null;
    /**
     * Key
     */
    key: string;
    /**
     * Description
     */
    description: string | null;
    /**
     * Enabled
     */
    enabled: boolean;
    /**
     * Everyone
     */
    everyone: boolean;
    /**
     * Roles
     */
    roles: Array<string>;
    /**
     * User Ids
     */
    user_ids: Array<string>;
};

/**
 * FeatureFlagUpdateRequest
 */
export type FeatureFlagUpdateRequest = {
    /**
     * Key
     */
    key?: string | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Enabled
     */
    enabled?: boolean | null;
    /**
     * Everyone
     */
    everyone?: boolean | null;
    /**
     * Roles
     */
    roles?: Array<string> | null;
    /**
     * User Ids
     */
    user_ids?: Array<string> | null;
};

/**
 * FolderLinksOrder
 */
export type FolderLinksOrder = {
    /**
     * Folder Id
     */
    folder_id: string;
    /**
     * Link Ids
     */
    link_ids: Array<string>;
};

/**
 * GrantPermissionRequest
 */
export type GrantPermissionRequest = {
    /**
     * Entity Type
     */
    entity_type: string;
    /**
     * Entity Id
     */
    entity_id: string;
    /**
     * Relation
     */
    relation: string;
    /**
     * Subject Type
     */
    subject_type: string;
    /**
     * Subject Id
     */
    subject_id: string;
    /**
     * Schema Version
     */
    schema_version?: string | null;
};

/**
 * HTTPValidationError
 */
export type HttpValidationError = {
    /**
     * Detail
     */
    detail?: Array<ValidationError>;
};

/**
 * HoldReason
 */
export const HoldReason = {
    PENDING_ACTIVATION: 'pending_activation',
    CONTRACT_REQUIRED: 'contract_required',
    OVERDUE_BALANCE: 'overdue_balance'
} as const;

/**
 * HoldReason
 */
export type HoldReason = typeof HoldReason[keyof typeof HoldReason];

/**
 * HubFolderItem
 */
export type HubFolderItem = {
    /**
     * Id
     */
    id: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at?: string | null;
    /**
     * Deleted At
     */
    deleted_at?: string | null;
    /**
     * Title
     */
    title: string;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Icon
     */
    icon?: string | null;
    section: LinkSection;
    /**
     * Visible To Roles
     */
    visible_to_roles?: Array<string>;
    /**
     * Sort Order
     */
    sort_order?: number;
    /**
     * Kind
     */
    kind: 'folder';
    /**
     * Links
     */
    links: Array<LinkSchema>;
};

/**
 * HubItemRef
 */
export type HubItemRef = {
    /**
     * Kind
     */
    kind: 'link' | 'folder';
    /**
     * Id
     */
    id: string;
};

/**
 * HubItemsReorderRequest
 */
export type HubItemsReorderRequest = {
    /**
     * Sections
     */
    sections?: Array<SectionItemsOrder>;
    /**
     * Folders
     */
    folders?: Array<FolderLinksOrder>;
};

/**
 * HubLinkItem
 */
export type HubLinkItem = {
    /**
     * Id
     */
    id: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at?: string | null;
    /**
     * Deleted At
     */
    deleted_at?: string | null;
    /**
     * Title
     */
    title: string;
    /**
     * Url
     */
    url: string;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Icon
     */
    icon?: string | null;
    section: LinkSection;
    /**
     * Visible To Roles
     */
    visible_to_roles?: Array<string>;
    /**
     * Sort Order
     */
    sort_order?: number;
    /**
     * Folder Id
     */
    folder_id?: string | null;
    /**
     * Kind
     */
    kind: 'link';
};

/**
 * InternalCompanySchema
 *
 * Schema for internal company data.
 */
export type InternalCompanySchema = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * On Hold
     */
    on_hold?: boolean;
    on_hold_reason?: HoldReason | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Last Updated At
     */
    last_updated_at?: string | null;
    /**
     * Deleted At
     */
    deleted_at?: string | null;
};

/**
 * InternalFacilitySchema
 *
 * Schema for internal facility data.
 */
export type InternalFacilitySchema = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * City
     */
    city: string;
    /**
     * State
     */
    state: string;
    /**
     * Zip Code
     */
    zip_code: string;
    /**
     * On Hold
     */
    on_hold?: boolean;
    on_hold_reason?: HoldReason | null;
    company?: InternalCompanySchema | null;
    /**
     * Clinic Days
     */
    clinic_days?: Array<ClinicDaySchema>;
    /**
     * Timezone
     */
    timezone: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Last Updated At
     */
    last_updated_at?: string | null;
    /**
     * Deleted At
     */
    deleted_at?: string | null;
};

/**
 * JobStatuses
 */
export const JobStatuses = {
    PENDING: 'Pending',
    INBOUND: 'Inbound',
    DISPATCHED: 'Dispatched',
    CHECKED_IN: 'Checked in',
    CLOSED: 'Closed',
    CANCELED: 'Canceled'
} as const;

/**
 * JobStatuses
 */
export type JobStatuses = typeof JobStatuses[keyof typeof JobStatuses];

/**
 * JobTypes
 */
export const JobTypes = {
    INFUSION: 'Infusion',
    VASCULAR_ACCESS: 'Vascular Access',
    TRANSFUSION: 'Transfusion'
} as const;

/**
 * JobTypes
 */
export type JobTypes = typeof JobTypes[keyof typeof JobTypes];

/**
 * Leaf
 */
export type Leaf = {
    computed_user_set?: ComputedUserSet | null;
    tuple_to_user_set?: TupleToUserSet | null;
};

/**
 * LinkCreateRequest
 */
export type LinkCreateRequest = {
    /**
     * Title
     */
    title: string;
    /**
     * Url
     */
    url: string;
    section: LinkSection;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Icon
     */
    icon?: string | null;
    /**
     * Visible To Roles
     */
    visible_to_roles?: Array<string>;
    /**
     * Sort Order
     */
    sort_order?: number;
    /**
     * Folder Id
     */
    folder_id?: string | null;
};

/**
 * LinkFolderCreateRequest
 */
export type LinkFolderCreateRequest = {
    /**
     * Title
     */
    title: string;
    section: LinkSection;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Icon
     */
    icon?: string | null;
    /**
     * Visible To Roles
     */
    visible_to_roles?: Array<string>;
    /**
     * Sort Order
     */
    sort_order?: number;
};

/**
 * LinkFolderSchema
 */
export type LinkFolderSchema = {
    /**
     * Id
     */
    id: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at?: string | null;
    /**
     * Deleted At
     */
    deleted_at?: string | null;
    /**
     * Title
     */
    title: string;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Icon
     */
    icon?: string | null;
    section: LinkSection;
    /**
     * Visible To Roles
     */
    visible_to_roles?: Array<string>;
    /**
     * Sort Order
     */
    sort_order?: number;
};

/**
 * LinkFolderUpdateRequest
 */
export type LinkFolderUpdateRequest = {
    /**
     * Title
     */
    title?: string | null;
    section?: LinkSection | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Icon
     */
    icon?: string | null;
    /**
     * Visible To Roles
     */
    visible_to_roles?: Array<string> | null;
    /**
     * Sort Order
     */
    sort_order?: number | null;
};

/**
 * LinkSchema
 */
export type LinkSchema = {
    /**
     * Id
     */
    id: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at?: string | null;
    /**
     * Deleted At
     */
    deleted_at?: string | null;
    /**
     * Title
     */
    title: string;
    /**
     * Url
     */
    url: string;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Icon
     */
    icon?: string | null;
    section: LinkSection;
    /**
     * Visible To Roles
     */
    visible_to_roles?: Array<string>;
    /**
     * Sort Order
     */
    sort_order?: number;
    /**
     * Folder Id
     */
    folder_id?: string | null;
};

/**
 * LinkSection
 */
export const LinkSection = { INTERNAL: 'Internal', EXTERNAL: 'External' } as const;

/**
 * LinkSection
 */
export type LinkSection = typeof LinkSection[keyof typeof LinkSection];

/**
 * LinkUpdateRequest
 */
export type LinkUpdateRequest = {
    /**
     * Title
     */
    title?: string | null;
    /**
     * Url
     */
    url?: string | null;
    section?: LinkSection | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Icon
     */
    icon?: string | null;
    /**
     * Visible To Roles
     */
    visible_to_roles?: Array<string> | null;
    /**
     * Sort Order
     */
    sort_order?: number | null;
    /**
     * Folder Id
     */
    folder_id?: string | null;
};

/**
 * MaintenanceAppStatus
 */
export type MaintenanceAppStatus = {
    /**
     * App
     */
    app: string;
    /**
     * Enabled
     */
    enabled: boolean;
};

/**
 * MaintenanceStatus
 */
export type MaintenanceStatus = {
    /**
     * Environment
     */
    environment: string;
    /**
     * Apps
     */
    apps: Array<MaintenanceAppStatus>;
};

/**
 * MaintenanceUpdateRequest
 */
export type MaintenanceUpdateRequest = {
    /**
     * Enabled
     */
    enabled: boolean;
};

/**
 * MetabaseGuestTokenRequest
 */
export type MetabaseGuestTokenRequest = {
    /**
     * Question Id
     */
    question_id: number;
};

/**
 * MetabaseGuestTokenResponse
 */
export type MetabaseGuestTokenResponse = {
    /**
     * Token
     */
    token: string;
};

/**
 * PermifySchemaSummary
 */
export type PermifySchemaSummary = {
    /**
     * Version
     *
     * Schema version id, or null when the tenant has no schema yet
     */
    version?: string | null;
    /**
     * Entities
     *
     * Entities with relations and permission expressions
     */
    entities?: Array<EntitySummary>;
};

/**
 * PermissionDefinition
 */
export type PermissionDefinition = {
    /**
     * Name
     */
    name: string;
    child: Child;
};

/**
 * PermissionSummary
 */
export type PermissionSummary = {
    /**
     * Name
     */
    name: string;
    /**
     * Expression
     */
    expression: string;
};

/**
 * ProcedureType
 */
export const ProcedureType = {
    INFUSION: 'Infusion',
    VASCULAR_ACCESS: 'Vascular Access',
    TRANSFUSION: 'Transfusion'
} as const;

/**
 * ProcedureType
 */
export type ProcedureType = typeof ProcedureType[keyof typeof ProcedureType];

/**
 * ReadRelationshipsRequestBody
 */
export type ReadRelationshipsRequestBody = {
    filter?: RelationshipTupleFilterBody | null;
    /**
     * Snap Token
     */
    snap_token?: string;
};

/**
 * ReadRelationshipsResponseBody
 */
export type ReadRelationshipsResponseBody = {
    /**
     * Tuples
     */
    tuples: Array<StoredRelationshipTupleBody>;
};

/**
 * RelationDefinition
 */
export type RelationDefinition = {
    /**
     * Name
     */
    name: string;
    /**
     * Relation References
     */
    relation_references?: Array<RelationReference>;
};

/**
 * RelationReference
 */
export type RelationReference = {
    /**
     * Type
     */
    type: string;
    /**
     * Relation
     */
    relation?: string;
};

/**
 * RelationSummary
 */
export type RelationSummary = {
    /**
     * Name
     */
    name: string;
    /**
     * Subject Types
     */
    subject_types: Array<string>;
};

/**
 * RelationshipTupleFilterBody
 */
export type RelationshipTupleFilterBody = {
    entity?: EntityFilterBody | null;
    /**
     * Relation
     */
    relation?: string | null;
    subject?: SubjectFilterBody | null;
};

/**
 * ReportCreateRequest
 */
export type ReportCreateRequest = {
    /**
     * Title
     */
    title: string;
    /**
     * Slug
     */
    slug: string;
    /**
     * Metabase Question Id
     */
    metabase_question_id: number;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Icon
     */
    icon?: string | null;
    cadence?: SubscriptionType | null;
    scope?: ReportScope;
    /**
     * Visible To Roles
     */
    visible_to_roles?: Array<string>;
    /**
     * Sort Order
     */
    sort_order?: number;
    /**
     * Restrict To User Regions
     */
    restrict_to_user_regions?: boolean;
};

/**
 * ReportSchema
 */
export type ReportSchema = {
    /**
     * Id
     */
    id: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at?: string | null;
    /**
     * Deleted At
     */
    deleted_at?: string | null;
    /**
     * Title
     */
    title: string;
    /**
     * Slug
     */
    slug: string;
    /**
     * Description
     */
    description: string | null;
    /**
     * Icon
     */
    icon: string | null;
    /**
     * Metabase Question Id
     */
    metabase_question_id: number;
    cadence: SubscriptionType | null;
    scope: ReportScope;
    /**
     * Visible To Roles
     */
    visible_to_roles: Array<string>;
    /**
     * Sort Order
     */
    sort_order: number;
    /**
     * Protected
     */
    protected: boolean;
    /**
     * Restrict To User Regions
     */
    restrict_to_user_regions: boolean;
};

/**
 * ReportScope
 *
 * What a viewer must pick before a report renders.
 *
 * ``none`` is a report that stands on its own — no pickers, and no company
 * or facility filter passed to Metabase. Subscriptions need a company, so
 * only company-scoped reports can be emailed on one.
 */
export const ReportScope = {
    NONE: 'None',
    COMPANY: 'Company',
    COMPANY_FACILITY: 'CompanyFacility'
} as const;

/**
 * ReportScope
 *
 * What a viewer must pick before a report renders.
 *
 * ``none`` is a report that stands on its own — no pickers, and no company
 * or facility filter passed to Metabase. Subscriptions need a company, so
 * only company-scoped reports can be emailed on one.
 */
export type ReportScope = typeof ReportScope[keyof typeof ReportScope];

/**
 * ReportUpdateRequest
 */
export type ReportUpdateRequest = {
    /**
     * Title
     */
    title?: string | null;
    /**
     * Slug
     */
    slug?: string | null;
    /**
     * Metabase Question Id
     */
    metabase_question_id?: number | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Icon
     */
    icon?: string | null;
    cadence?: SubscriptionType | null;
    scope?: ReportScope | null;
    /**
     * Visible To Roles
     */
    visible_to_roles?: Array<string> | null;
    /**
     * Sort Order
     */
    sort_order?: number | null;
    /**
     * Restrict To User Regions
     */
    restrict_to_user_regions?: boolean | null;
};

/**
 * ReportsReorderRequest
 */
export type ReportsReorderRequest = {
    /**
     * Report Ids
     */
    report_ids: Array<string>;
};

/**
 * Rewrite
 */
export type Rewrite = {
    rewrite_operation: RewriteOperation;
    /**
     * Children
     */
    children?: Array<Child>;
};

/**
 * RewriteOperation
 */
export const RewriteOperation = {
    OPERATION_UNSPECIFIED: 'OPERATION_UNSPECIFIED',
    OPERATION_UNION: 'OPERATION_UNION',
    OPERATION_INTERSECTION: 'OPERATION_INTERSECTION',
    OPERATION_EXCLUSION: 'OPERATION_EXCLUSION'
} as const;

/**
 * RewriteOperation
 */
export type RewriteOperation = typeof RewriteOperation[keyof typeof RewriteOperation];

/**
 * Schema
 */
export type Schema = {
    /**
     * Version
     */
    version: string;
    /**
     * Created At
     */
    created_at: string;
};

/**
 * SchemaDefinition
 */
export type SchemaDefinition = {
    /**
     * Entity Definitions
     */
    entity_definitions?: {
        [key: string]: EntityDefinition;
    };
    /**
     * Rule Definitions
     */
    rule_definitions?: {
        [key: string]: unknown;
    };
    /**
     * References
     */
    references?: {
        [key: string]: SchemaReferenceKind;
    };
};

/**
 * SchemaReferenceKind
 */
export const SchemaReferenceKind = { REFERENCE_UNSPECIFIED: 'REFERENCE_UNSPECIFIED', REFERENCE_ENTITY: 'REFERENCE_ENTITY' } as const;

/**
 * SchemaReferenceKind
 */
export type SchemaReferenceKind = typeof SchemaReferenceKind[keyof typeof SchemaReferenceKind];

/**
 * SectionItemsOrder
 */
export type SectionItemsOrder = {
    section: LinkSection;
    /**
     * Items
     */
    items: Array<HubItemRef>;
};

/**
 * SendSubscriptionRequest
 *
 * The recipients the operator confirmed for an on-demand send.
 */
export type SendSubscriptionRequest = {
    /**
     * To Emails
     */
    to_emails: Array<string>;
};

/**
 * StoredRelationshipTupleBody
 */
export type StoredRelationshipTupleBody = {
    /**
     * Entity Type
     */
    entity_type: string;
    /**
     * Entity Id
     */
    entity_id: string;
    /**
     * Relation
     */
    relation: string;
    /**
     * Subject Type
     */
    subject_type: string;
    /**
     * Subject Id
     */
    subject_id: string;
    /**
     * Subject Relation
     */
    subject_relation?: string;
};

/**
 * SubjectFilterBody
 */
export type SubjectFilterBody = {
    /**
     * Type
     */
    type?: string | null;
    /**
     * Ids
     */
    ids?: Array<string> | null;
    /**
     * Relation
     */
    relation?: string | null;
};

/**
 * SubscriptionAutoRecipientSchema
 */
export type SubscriptionAutoRecipientSchema = {
    /**
     * Name
     */
    name: string;
    /**
     * Email
     */
    email: string;
    role: AutoRecipientRole;
    /**
     * Phone Number
     */
    phone_number?: string | null;
};

/**
 * SubscriptionContactSchema
 */
export type SubscriptionContactSchema = {
    /**
     * Name
     */
    name: string;
    /**
     * Role
     */
    role: string;
    /**
     * Email
     */
    email: string;
};

/**
 * SubscriptionCreateRequest
 */
export type SubscriptionCreateRequest = {
    /**
     * To Emails
     */
    to_emails: Array<string>;
    typ: SubscriptionType;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Facility Id
     */
    facility_id?: string | null;
    /**
     * Metabase Report Id
     */
    metabase_report_id: string;
    /**
     * Send Delay Days
     */
    send_delay_days: number;
    /**
     * Send Hour
     */
    send_hour: number;
};

/**
 * SubscriptionSendHistorySchema
 */
export type SubscriptionSendHistorySchema = {
    /**
     * Id
     */
    id: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at?: string | null;
    /**
     * Deleted At
     */
    deleted_at?: string | null;
    /**
     * Subscription Id
     */
    subscription_id: string;
    /**
     * To Emails
     */
    to_emails: Array<string>;
    /**
     * Body
     */
    body: string;
    /**
     * Path
     */
    path: string;
};

/**
 * SubscriptionType
 */
export const SubscriptionType = { WEEKLY: 'Weekly', DAILY: 'Daily' } as const;

/**
 * SubscriptionType
 */
export type SubscriptionType = typeof SubscriptionType[keyof typeof SubscriptionType];

/**
 * SubscriptionUpdateRequest
 */
export type SubscriptionUpdateRequest = {
    /**
     * To Emails
     */
    to_emails: Array<string>;
    typ: SubscriptionType;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Facility Id
     */
    facility_id?: string | null;
    /**
     * Metabase Report Id
     */
    metabase_report_id: string;
    /**
     * Send Delay Days
     */
    send_delay_days: number;
    /**
     * Send Hour
     */
    send_hour: number;
};

/**
 * SuggestDescriptionRequest
 */
export type SuggestDescriptionRequest = {
    /**
     * Subject
     */
    subject: string;
    /**
     * Context
     */
    context?: string | null;
    /**
     * Current Text
     */
    current_text?: string | null;
};

/**
 * SuggestDescriptionResult
 */
export type SuggestDescriptionResult = {
    /**
     * Suggestion
     */
    suggestion: string;
};

/**
 * SuggestIconsRequest
 */
export type SuggestIconsRequest = {
    /**
     * Subject
     */
    subject: string;
    /**
     * Context
     */
    context?: string | null;
    /**
     * Candidates
     */
    candidates: Array<string>;
    /**
     * Limit
     */
    limit?: number;
};

/**
 * SuggestIconsResult
 */
export type SuggestIconsResult = {
    /**
     * Icons
     */
    icons: Array<string>;
};

/**
 * SyncUserPermifyResponse
 */
export type SyncUserPermifyResponse = {
    user: AppUserPermifyPermissions;
    /**
     * Granted
     *
     * Whether a new Permify tuple was granted during sync
     */
    granted: boolean;
};

/**
 * Tenant
 */
export type Tenant = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Created At
     */
    created_at: string;
};

/**
 * TupleSet
 */
export type TupleSet = {
    /**
     * Relation
     */
    relation: string;
};

/**
 * TupleToUserSet
 */
export type TupleToUserSet = {
    tupleSet: TupleSet;
    computed: ComputedRelation;
};

/**
 * USTimezones
 *
 * Canonical IANA time zone identifiers for the United States
 */
export const UsTimezones = {
    AMERICA_NEW_YORK: 'America/New_York',
    AMERICA_CHICAGO: 'America/Chicago',
    AMERICA_DENVER: 'America/Denver',
    AMERICA_LOS_ANGELES: 'America/Los_Angeles',
    AMERICA_PHOENIX: 'America/Phoenix'
} as const;

/**
 * USTimezones
 *
 * Canonical IANA time zone identifiers for the United States
 */
export type UsTimezones = typeof UsTimezones[keyof typeof UsTimezones];

/**
 * UserRoles
 */
export const UserRoles = {
    ADMIN: 'Admin',
    REGION_ADMIN: 'Region Admin',
    NURSE: 'Nurse',
    TRIAGE_NURSE: 'Triage Nurse',
    FIELD_NURSE: 'Field Nurse',
    FACILITY: 'Facility'
} as const;

/**
 * UserRoles
 */
export type UserRoles = typeof UserRoles[keyof typeof UserRoles];

/**
 * ValidationError
 */
export type ValidationError = {
    /**
     * Location
     */
    loc: Array<string | number>;
    /**
     * Message
     */
    msg: string;
    /**
     * Error Type
     */
    type: string;
    /**
     * Input
     */
    input?: unknown;
    /**
     * Context
     */
    ctx?: {
        [key: string]: unknown;
    };
};

/**
 * VellumAuthMeResponse
 *
 * Luna `User` fields exposed after Google SSO (avoids full `UserListSchema` requirements).
 */
export type VellumAuthMeResponse = {
    /**
     * Id
     */
    id: string;
    /**
     * Email
     */
    email: string;
    /**
     * Name
     */
    name: string;
    role?: UserRoles | null;
    /**
     * Phone Number
     */
    phone_number?: string | null;
    timezone?: UsTimezones | null;
    /**
     * Last Login
     */
    last_login?: string | null;
};

export type HealthyApiV1HealthyGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/healthy';
};

export type HealthyApiV1HealthyGetResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type HealthyApiV1HealthyHeadData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/healthy';
};

export type HealthyApiV1HealthyHeadResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type AdminListAnswerFirstRecipientsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/answer-first-recipients';
};

export type AdminListAnswerFirstRecipientsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type AdminListAnswerFirstRecipientsError = AdminListAnswerFirstRecipientsErrors[keyof AdminListAnswerFirstRecipientsErrors];

export type AdminListAnswerFirstRecipientsResponses = {
    /**
     * Response Adminlistanswerfirstrecipients
     *
     * Successful Response
     */
    200: Array<AdminAnswerFirstRecipientSchema>;
};

export type AdminListAnswerFirstRecipientsResponse = AdminListAnswerFirstRecipientsResponses[keyof AdminListAnswerFirstRecipientsResponses];

export type AdminCreateAnswerFirstRecipientData = {
    body: AdminCreateAnswerFirstRecipient;
    path?: never;
    query?: never;
    url: '/api/v1/admin/answer-first-recipients';
};

export type AdminCreateAnswerFirstRecipientErrors = {
    /**
     * Bad Request
     */
    400: ErrorDetail;
    /**
     * Not Found
     */
    404: ErrorDetail;
    /**
     * Conflict
     */
    409: ErrorDetail;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type AdminCreateAnswerFirstRecipientError = AdminCreateAnswerFirstRecipientErrors[keyof AdminCreateAnswerFirstRecipientErrors];

export type AdminCreateAnswerFirstRecipientResponses = {
    /**
     * Successful Response
     */
    201: AdminAnswerFirstRecipientSchema;
};

export type AdminCreateAnswerFirstRecipientResponse = AdminCreateAnswerFirstRecipientResponses[keyof AdminCreateAnswerFirstRecipientResponses];

export type AdminDeleteAnswerFirstRecipientData = {
    body?: never;
    path: {
        /**
         * Recipient Id
         */
        recipient_id: string;
    };
    query?: never;
    url: '/api/v1/admin/answer-first-recipients/{recipient_id}';
};

export type AdminDeleteAnswerFirstRecipientErrors = {
    /**
     * Not Found
     */
    404: ErrorDetail;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type AdminDeleteAnswerFirstRecipientError = AdminDeleteAnswerFirstRecipientErrors[keyof AdminDeleteAnswerFirstRecipientErrors];

export type AdminDeleteAnswerFirstRecipientResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminDeleteAnswerFirstRecipientResponse = AdminDeleteAnswerFirstRecipientResponses[keyof AdminDeleteAnswerFirstRecipientResponses];

export type AdminListCompaniesData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Limit
         *
         * Max rows to return
         */
        limit?: number;
        /**
         * Offset
         *
         * Rows to skip
         */
        offset?: number;
        /**
         * Q
         *
         * Case-insensitive substring match on company name
         */
        q?: string | null;
    };
    url: '/api/v1/admin/companies';
};

export type AdminListCompaniesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type AdminListCompaniesError = AdminListCompaniesErrors[keyof AdminListCompaniesErrors];

export type AdminListCompaniesResponses = {
    /**
     * Successful Response
     */
    200: AdminPaginatedCompanies;
};

export type AdminListCompaniesResponse = AdminListCompaniesResponses[keyof AdminListCompaniesResponses];

export type AdminGetCoverageMapData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Since
         *
         * Include anyone who did a visit on or after this date
         */
        since?: string | null;
    };
    url: '/api/v1/admin/coverage/map';
};

export type AdminGetCoverageMapErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type AdminGetCoverageMapError = AdminGetCoverageMapErrors[keyof AdminGetCoverageMapErrors];

export type AdminGetCoverageMapResponses = {
    /**
     * Successful Response
     */
    200: CoverageMap;
};

export type AdminGetCoverageMapResponse = AdminGetCoverageMapResponses[keyof AdminGetCoverageMapResponses];

export type AdminGetCoverageVolumeData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Since
         *
         * Count activity on or after this date (UTC)
         */
        since?: string | null;
    };
    url: '/api/v1/admin/coverage/volume';
};

export type AdminGetCoverageVolumeErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type AdminGetCoverageVolumeError = AdminGetCoverageVolumeErrors[keyof AdminGetCoverageVolumeErrors];

export type AdminGetCoverageVolumeResponses = {
    /**
     * Successful Response
     */
    200: CoverageVolume;
};

export type AdminGetCoverageVolumeResponse = AdminGetCoverageVolumeResponses[keyof AdminGetCoverageVolumeResponses];

export type AdminGetCoverageProspectsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/coverage/prospects';
};

export type AdminGetCoverageProspectsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type AdminGetCoverageProspectsError = AdminGetCoverageProspectsErrors[keyof AdminGetCoverageProspectsErrors];

export type AdminGetCoverageProspectsResponses = {
    /**
     * Successful Response
     */
    200: CoverageProspects;
};

export type AdminGetCoverageProspectsResponse = AdminGetCoverageProspectsResponses[keyof AdminGetCoverageProspectsResponses];

export type AdminGetDispatchBoardData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Region Ids
         *
         * Only facilities in these regions; every region when omitted
         */
        region_ids?: Array<string> | null;
    };
    url: '/api/v1/admin/dispatch/board';
};

export type AdminGetDispatchBoardErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type AdminGetDispatchBoardError = AdminGetDispatchBoardErrors[keyof AdminGetDispatchBoardErrors];

export type AdminGetDispatchBoardResponses = {
    /**
     * Successful Response
     */
    200: DispatchBoard;
};

export type AdminGetDispatchBoardResponse = AdminGetDispatchBoardResponses[keyof AdminGetDispatchBoardResponses];

export type AdminGetDispatchNurseRouteData = {
    body?: never;
    path: {
        /**
         * Nurse Id
         */
        nurse_id: string;
    };
    query?: never;
    url: '/api/v1/admin/dispatch/nurses/{nurse_id}/route';
};

export type AdminGetDispatchNurseRouteErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type AdminGetDispatchNurseRouteError = AdminGetDispatchNurseRouteErrors[keyof AdminGetDispatchNurseRouteErrors];

export type AdminGetDispatchNurseRouteResponses = {
    /**
     * Successful Response
     */
    200: DispatchNurseRoute;
};

export type AdminGetDispatchNurseRouteResponse = AdminGetDispatchNurseRouteResponses[keyof AdminGetDispatchNurseRouteResponses];

export type AdminListFacilitiesData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Company Id
         *
         * Filter by company ID
         */
        company_id?: string | null;
        /**
         * Limit
         *
         * Max rows to return
         */
        limit?: number;
        /**
         * Offset
         *
         * Rows to skip
         */
        offset?: number;
        /**
         * Q
         *
         * Case-insensitive substring match on facility name or city
         */
        q?: string | null;
    };
    url: '/api/v1/admin/facilities';
};

export type AdminListFacilitiesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type AdminListFacilitiesError = AdminListFacilitiesErrors[keyof AdminListFacilitiesErrors];

export type AdminListFacilitiesResponses = {
    /**
     * Successful Response
     */
    200: AdminPaginatedFacilities;
};

export type AdminListFacilitiesResponse = AdminListFacilitiesResponses[keyof AdminListFacilitiesResponses];

export type AdminSearchUserOptionsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Q
         *
         * Case-insensitive match on name or email
         */
        q?: string | null;
        /**
         * Ids
         *
         * Resolve these user ids instead
         */
        ids?: Array<string> | null;
    };
    url: '/api/v1/admin/user-options';
};

export type AdminSearchUserOptionsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type AdminSearchUserOptionsError = AdminSearchUserOptionsErrors[keyof AdminSearchUserOptionsErrors];

export type AdminSearchUserOptionsResponses = {
    /**
     * Response Adminsearchuseroptions
     *
     * Successful Response
     */
    200: Array<AdminUserOption>;
};

export type AdminSearchUserOptionsResponse = AdminSearchUserOptionsResponses[keyof AdminSearchUserOptionsResponses];

export type AdminListUsersData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Limit
         *
         * Max rows to return
         */
        limit?: number;
        /**
         * Offset
         *
         * Rows to skip
         */
        offset?: number;
        /**
         * Q
         *
         * Case-insensitive substring match on name or email
         */
        q?: string | null;
    };
    url: '/api/v1/admin/users';
};

export type AdminListUsersErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type AdminListUsersError = AdminListUsersErrors[keyof AdminListUsersErrors];

export type AdminListUsersResponses = {
    /**
     * Successful Response
     */
    200: AdminPaginatedUsers;
};

export type AdminListUsersResponse = AdminListUsersResponses[keyof AdminListUsersResponses];

export type SuggestDescriptionData = {
    body: SuggestDescriptionRequest;
    path?: never;
    query?: never;
    url: '/api/v1/ai/suggest-description';
};

export type SuggestDescriptionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type SuggestDescriptionError = SuggestDescriptionErrors[keyof SuggestDescriptionErrors];

export type SuggestDescriptionResponses = {
    /**
     * Successful Response
     */
    200: SuggestDescriptionResult;
};

export type SuggestDescriptionResponse = SuggestDescriptionResponses[keyof SuggestDescriptionResponses];

export type SuggestIconsData = {
    body: SuggestIconsRequest;
    path?: never;
    query?: never;
    url: '/api/v1/ai/suggest-icons';
};

export type SuggestIconsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type SuggestIconsError = SuggestIconsErrors[keyof SuggestIconsErrors];

export type SuggestIconsResponses = {
    /**
     * Successful Response
     */
    200: SuggestIconsResult;
};

export type SuggestIconsResponse = SuggestIconsResponses[keyof SuggestIconsResponses];

export type WhoamiData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/';
};

export type WhoamiErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type WhoamiError = WhoamiErrors[keyof WhoamiErrors];

export type WhoamiResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GenerateOAuthCallbackData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/auth/generate-callback';
};

export type GenerateOAuthCallbackResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GoogleOAuthCallbackData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Code
         */
        code?: string | null;
        /**
         * Error
         */
        error?: string | null;
    };
    url: '/api/v1/auth/callback/google';
};

export type GoogleOAuthCallbackErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GoogleOAuthCallbackError = GoogleOAuthCallbackErrors[keyof GoogleOAuthCallbackErrors];

export type GoogleOAuthCallbackResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type LogoutData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/auth/logout';
};

export type LogoutResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type LogoutResponse = LogoutResponses[keyof LogoutResponses];

export type GetCurrentUserData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/auth/me';
};

export type GetCurrentUserErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCurrentUserError = GetCurrentUserErrors[keyof GetCurrentUserErrors];

export type GetCurrentUserResponses = {
    /**
     * Successful Response
     */
    200: VellumAuthMeResponse;
};

export type GetCurrentUserResponse = GetCurrentUserResponses[keyof GetCurrentUserResponses];

export type ListFeatureFlagsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/flags';
};

export type ListFeatureFlagsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ListFeatureFlagsError = ListFeatureFlagsErrors[keyof ListFeatureFlagsErrors];

export type ListFeatureFlagsResponses = {
    /**
     * Response Listfeatureflags
     *
     * Successful Response
     */
    200: Array<FeatureFlagSchema>;
};

export type ListFeatureFlagsResponse = ListFeatureFlagsResponses[keyof ListFeatureFlagsResponses];

export type CreateFeatureFlagData = {
    body: FeatureFlagCreateRequest;
    path?: never;
    query?: never;
    url: '/api/v1/flags';
};

export type CreateFeatureFlagErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type CreateFeatureFlagError = CreateFeatureFlagErrors[keyof CreateFeatureFlagErrors];

export type CreateFeatureFlagResponses = {
    /**
     * Successful Response
     */
    201: FeatureFlagSchema;
};

export type CreateFeatureFlagResponse = CreateFeatureFlagResponses[keyof CreateFeatureFlagResponses];

export type DeleteFeatureFlagData = {
    body?: never;
    path: {
        /**
         * Flag Id
         */
        flag_id: string;
    };
    query?: never;
    url: '/api/v1/flags/{flag_id}';
};

export type DeleteFeatureFlagErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteFeatureFlagError = DeleteFeatureFlagErrors[keyof DeleteFeatureFlagErrors];

export type DeleteFeatureFlagResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteFeatureFlagResponse = DeleteFeatureFlagResponses[keyof DeleteFeatureFlagResponses];

export type UpdateFeatureFlagData = {
    body: FeatureFlagUpdateRequest;
    path: {
        /**
         * Flag Id
         */
        flag_id: string;
    };
    query?: never;
    url: '/api/v1/flags/{flag_id}';
};

export type UpdateFeatureFlagErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UpdateFeatureFlagError = UpdateFeatureFlagErrors[keyof UpdateFeatureFlagErrors];

export type UpdateFeatureFlagResponses = {
    /**
     * Successful Response
     */
    200: FeatureFlagSchema;
};

export type UpdateFeatureFlagResponse = UpdateFeatureFlagResponses[keyof UpdateFeatureFlagResponses];

export type GetMyFeatureFlagsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/flags/me';
};

export type GetMyFeatureFlagsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetMyFeatureFlagsError = GetMyFeatureFlagsErrors[keyof GetMyFeatureFlagsErrors];

export type GetMyFeatureFlagsResponses = {
    /**
     * Response Getmyfeatureflags
     *
     * Successful Response
     */
    200: {
        [key: string]: boolean;
    };
};

export type GetMyFeatureFlagsResponse = GetMyFeatureFlagsResponses[keyof GetMyFeatureFlagsResponses];

export type ListHubItemsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/hub/items';
};

export type ListHubItemsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ListHubItemsError = ListHubItemsErrors[keyof ListHubItemsErrors];

export type ListHubItemsResponses = {
    /**
     * Response Listhubitems
     *
     * Successful Response
     */
    200: Array<HubLinkItem | HubFolderItem>;
};

export type ListHubItemsResponse = ListHubItemsResponses[keyof ListHubItemsResponses];

export type ReorderHubItemsData = {
    body: HubItemsReorderRequest;
    path?: never;
    query?: never;
    url: '/api/v1/hub/items/order';
};

export type ReorderHubItemsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ReorderHubItemsError = ReorderHubItemsErrors[keyof ReorderHubItemsErrors];

export type ReorderHubItemsResponses = {
    /**
     * Response Reorderhubitems
     *
     * Successful Response
     */
    200: Array<HubLinkItem | HubFolderItem>;
};

export type ReorderHubItemsResponse = ReorderHubItemsResponses[keyof ReorderHubItemsResponses];

export type CreateLinkFolderData = {
    body: LinkFolderCreateRequest;
    path?: never;
    query?: never;
    url: '/api/v1/link-folders';
};

export type CreateLinkFolderErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type CreateLinkFolderError = CreateLinkFolderErrors[keyof CreateLinkFolderErrors];

export type CreateLinkFolderResponses = {
    /**
     * Successful Response
     */
    201: LinkFolderSchema;
};

export type CreateLinkFolderResponse = CreateLinkFolderResponses[keyof CreateLinkFolderResponses];

export type DeleteLinkFolderData = {
    body?: never;
    path: {
        /**
         * Folder Id
         */
        folder_id: string;
    };
    query?: never;
    url: '/api/v1/link-folders/{folder_id}';
};

export type DeleteLinkFolderErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteLinkFolderError = DeleteLinkFolderErrors[keyof DeleteLinkFolderErrors];

export type DeleteLinkFolderResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteLinkFolderResponse = DeleteLinkFolderResponses[keyof DeleteLinkFolderResponses];

export type UpdateLinkFolderData = {
    body: LinkFolderUpdateRequest;
    path: {
        /**
         * Folder Id
         */
        folder_id: string;
    };
    query?: never;
    url: '/api/v1/link-folders/{folder_id}';
};

export type UpdateLinkFolderErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UpdateLinkFolderError = UpdateLinkFolderErrors[keyof UpdateLinkFolderErrors];

export type UpdateLinkFolderResponses = {
    /**
     * Successful Response
     */
    200: LinkFolderSchema;
};

export type UpdateLinkFolderResponse = UpdateLinkFolderResponses[keyof UpdateLinkFolderResponses];

export type CreateLinkData = {
    body: LinkCreateRequest;
    path?: never;
    query?: never;
    url: '/api/v1/links';
};

export type CreateLinkErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type CreateLinkError = CreateLinkErrors[keyof CreateLinkErrors];

export type CreateLinkResponses = {
    /**
     * Successful Response
     */
    201: LinkSchema;
};

export type CreateLinkResponse = CreateLinkResponses[keyof CreateLinkResponses];

export type DeleteLinkData = {
    body?: never;
    path: {
        /**
         * Link Id
         */
        link_id: string;
    };
    query?: never;
    url: '/api/v1/links/{link_id}';
};

export type DeleteLinkErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteLinkError = DeleteLinkErrors[keyof DeleteLinkErrors];

export type DeleteLinkResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteLinkResponse = DeleteLinkResponses[keyof DeleteLinkResponses];

export type UpdateLinkData = {
    body: LinkUpdateRequest;
    path: {
        /**
         * Link Id
         */
        link_id: string;
    };
    query?: never;
    url: '/api/v1/links/{link_id}';
};

export type UpdateLinkErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UpdateLinkError = UpdateLinkErrors[keyof UpdateLinkErrors];

export type UpdateLinkResponses = {
    /**
     * Successful Response
     */
    200: LinkSchema;
};

export type UpdateLinkResponse = UpdateLinkResponses[keyof UpdateLinkResponses];

export type GetMaintenanceStatusData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/maintenance';
};

export type GetMaintenanceStatusErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetMaintenanceStatusError = GetMaintenanceStatusErrors[keyof GetMaintenanceStatusErrors];

export type GetMaintenanceStatusResponses = {
    /**
     * Successful Response
     */
    200: MaintenanceStatus;
};

export type GetMaintenanceStatusResponse = GetMaintenanceStatusResponses[keyof GetMaintenanceStatusResponses];

export type SetMaintenanceData = {
    body: MaintenanceUpdateRequest;
    path: {
        /**
         * App
         */
        app: string;
    };
    query?: never;
    url: '/api/v1/maintenance/{app}';
};

export type SetMaintenanceErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type SetMaintenanceError = SetMaintenanceErrors[keyof SetMaintenanceErrors];

export type SetMaintenanceResponses = {
    /**
     * Successful Response
     */
    200: MaintenanceAppStatus;
};

export type SetMaintenanceResponse = SetMaintenanceResponses[keyof SetMaintenanceResponses];

export type VellumApiListExpectedPermifyTenantsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/permify/expected-tenants';
};

export type VellumApiListExpectedPermifyTenantsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type VellumApiListExpectedPermifyTenantsError = VellumApiListExpectedPermifyTenantsErrors[keyof VellumApiListExpectedPermifyTenantsErrors];

export type VellumApiListExpectedPermifyTenantsResponses = {
    /**
     * Response Vellumapilistexpectedpermifytenants
     *
     * Successful Response
     */
    200: Array<ExpectedPermifyTenant>;
};

export type VellumApiListExpectedPermifyTenantsResponse = VellumApiListExpectedPermifyTenantsResponses[keyof VellumApiListExpectedPermifyTenantsResponses];

export type VellumApiApplyPermifyTenantData = {
    body?: never;
    path: {
        /**
         * Tenant Id
         */
        tenant_id: string;
    };
    query?: never;
    url: '/api/v1/permify/tenants/{tenant_id}/apply';
};

export type VellumApiApplyPermifyTenantErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type VellumApiApplyPermifyTenantError = VellumApiApplyPermifyTenantErrors[keyof VellumApiApplyPermifyTenantErrors];

export type VellumApiApplyPermifyTenantResponses = {
    /**
     * Successful Response
     */
    200: ExpectedPermifyTenant;
};

export type VellumApiApplyPermifyTenantResponse = VellumApiApplyPermifyTenantResponses[keyof VellumApiApplyPermifyTenantResponses];

export type VellumApiListPermifyTenantsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/permify/tenants';
};

export type VellumApiListPermifyTenantsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type VellumApiListPermifyTenantsError = VellumApiListPermifyTenantsErrors[keyof VellumApiListPermifyTenantsErrors];

export type VellumApiListPermifyTenantsResponses = {
    /**
     * Response Vellumapilistpermifytenants
     *
     * Successful Response
     */
    200: Array<Tenant>;
};

export type VellumApiListPermifyTenantsResponse = VellumApiListPermifyTenantsResponses[keyof VellumApiListPermifyTenantsResponses];

export type VellumApiCreatePermifyTenantData = {
    body: CreateTenantRequest;
    path?: never;
    query?: never;
    url: '/api/v1/permify/tenants';
};

export type VellumApiCreatePermifyTenantErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type VellumApiCreatePermifyTenantError = VellumApiCreatePermifyTenantErrors[keyof VellumApiCreatePermifyTenantErrors];

export type VellumApiCreatePermifyTenantResponses = {
    /**
     * Successful Response
     */
    200: Tenant;
};

export type VellumApiCreatePermifyTenantResponse = VellumApiCreatePermifyTenantResponses[keyof VellumApiCreatePermifyTenantResponses];

export type VellumApiGetPermifyTenantData = {
    body?: never;
    path: {
        /**
         * Tenant Id
         */
        tenant_id: string;
    };
    query?: never;
    url: '/api/v1/permify/tenants/{tenant_id}';
};

export type VellumApiGetPermifyTenantErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type VellumApiGetPermifyTenantError = VellumApiGetPermifyTenantErrors[keyof VellumApiGetPermifyTenantErrors];

export type VellumApiGetPermifyTenantResponses = {
    /**
     * Successful Response
     */
    200: Tenant;
};

export type VellumApiGetPermifyTenantResponse = VellumApiGetPermifyTenantResponses[keyof VellumApiGetPermifyTenantResponses];

export type VellumApiListPermifySchemasData = {
    body?: never;
    path: {
        /**
         * Tenant Id
         */
        tenant_id: string;
    };
    query?: never;
    url: '/api/v1/permify/tenants/{tenant_id}/schemas';
};

export type VellumApiListPermifySchemasErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type VellumApiListPermifySchemasError = VellumApiListPermifySchemasErrors[keyof VellumApiListPermifySchemasErrors];

export type VellumApiListPermifySchemasResponses = {
    /**
     * Response Vellumapilistpermifyschemas
     *
     * Successful Response
     */
    200: Array<Schema>;
};

export type VellumApiListPermifySchemasResponse = VellumApiListPermifySchemasResponses[keyof VellumApiListPermifySchemasResponses];

export type VellumApiCreatePermifySchemaData = {
    body: CreateSchemaRequest;
    path: {
        /**
         * Tenant Id
         */
        tenant_id: string;
    };
    query?: never;
    url: '/api/v1/permify/tenants/{tenant_id}/schemas';
};

export type VellumApiCreatePermifySchemaErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type VellumApiCreatePermifySchemaError = VellumApiCreatePermifySchemaErrors[keyof VellumApiCreatePermifySchemaErrors];

export type VellumApiCreatePermifySchemaResponses = {
    /**
     * Response Vellumapicreatepermifyschema
     *
     * Successful Response
     */
    200: {
        [key: string]: string;
    };
};

export type VellumApiCreatePermifySchemaResponse = VellumApiCreatePermifySchemaResponses[keyof VellumApiCreatePermifySchemaResponses];

export type VellumApiGetPermifySchemaHeadData = {
    body?: never;
    path: {
        /**
         * Tenant Id
         */
        tenant_id: string;
    };
    query?: never;
    url: '/api/v1/permify/tenants/{tenant_id}/schemas/head';
};

export type VellumApiGetPermifySchemaHeadErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type VellumApiGetPermifySchemaHeadError = VellumApiGetPermifySchemaHeadErrors[keyof VellumApiGetPermifySchemaHeadErrors];

export type VellumApiGetPermifySchemaHeadResponses = {
    /**
     * Response Vellumapigetpermifyschemahead
     *
     * Successful Response
     */
    200: {
        [key: string]: string | null;
    };
};

export type VellumApiGetPermifySchemaHeadResponse = VellumApiGetPermifySchemaHeadResponses[keyof VellumApiGetPermifySchemaHeadResponses];

export type VellumApiGetPermifySchemaHeadSummaryData = {
    body?: never;
    path: {
        /**
         * Tenant Id
         */
        tenant_id: string;
    };
    query?: never;
    url: '/api/v1/permify/tenants/{tenant_id}/schemas/head/summary';
};

export type VellumApiGetPermifySchemaHeadSummaryErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type VellumApiGetPermifySchemaHeadSummaryError = VellumApiGetPermifySchemaHeadSummaryErrors[keyof VellumApiGetPermifySchemaHeadSummaryErrors];

export type VellumApiGetPermifySchemaHeadSummaryResponses = {
    /**
     * Successful Response
     */
    200: PermifySchemaSummary;
};

export type VellumApiGetPermifySchemaHeadSummaryResponse = VellumApiGetPermifySchemaHeadSummaryResponses[keyof VellumApiGetPermifySchemaHeadSummaryResponses];

export type VellumApiGetPermifySchemaSummaryData = {
    body?: never;
    path: {
        /**
         * Tenant Id
         */
        tenant_id: string;
        /**
         * Version
         */
        version: string;
    };
    query?: never;
    url: '/api/v1/permify/tenants/{tenant_id}/schemas/{version}/summary';
};

export type VellumApiGetPermifySchemaSummaryErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type VellumApiGetPermifySchemaSummaryError = VellumApiGetPermifySchemaSummaryErrors[keyof VellumApiGetPermifySchemaSummaryErrors];

export type VellumApiGetPermifySchemaSummaryResponses = {
    /**
     * Successful Response
     */
    200: PermifySchemaSummary;
};

export type VellumApiGetPermifySchemaSummaryResponse = VellumApiGetPermifySchemaSummaryResponses[keyof VellumApiGetPermifySchemaSummaryResponses];

export type VellumApiGetPermifySchemaData = {
    body?: never;
    path: {
        /**
         * Tenant Id
         */
        tenant_id: string;
        /**
         * Version
         */
        version: string;
    };
    query?: never;
    url: '/api/v1/permify/tenants/{tenant_id}/schemas/{version}';
};

export type VellumApiGetPermifySchemaErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type VellumApiGetPermifySchemaError = VellumApiGetPermifySchemaErrors[keyof VellumApiGetPermifySchemaErrors];

export type VellumApiGetPermifySchemaResponses = {
    /**
     * Successful Response
     */
    200: SchemaDefinition;
};

export type VellumApiGetPermifySchemaResponse = VellumApiGetPermifySchemaResponses[keyof VellumApiGetPermifySchemaResponses];

export type VellumApiReadPermifyRelationshipsData = {
    body: ReadRelationshipsRequestBody;
    path: {
        /**
         * Tenant Id
         */
        tenant_id: string;
    };
    query?: never;
    url: '/api/v1/permify/tenants/{tenant_id}/relationships/read';
};

export type VellumApiReadPermifyRelationshipsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type VellumApiReadPermifyRelationshipsError = VellumApiReadPermifyRelationshipsErrors[keyof VellumApiReadPermifyRelationshipsErrors];

export type VellumApiReadPermifyRelationshipsResponses = {
    /**
     * Successful Response
     */
    200: ReadRelationshipsResponseBody;
};

export type VellumApiReadPermifyRelationshipsResponse = VellumApiReadPermifyRelationshipsResponses[keyof VellumApiReadPermifyRelationshipsResponses];

export type VellumApiCheckPermifyPermissionData = {
    body: CheckPermissionRequestBody;
    path: {
        /**
         * Tenant Id
         */
        tenant_id: string;
    };
    query?: never;
    url: '/api/v1/permify/tenants/{tenant_id}/permissions/check';
};

export type VellumApiCheckPermifyPermissionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type VellumApiCheckPermifyPermissionError = VellumApiCheckPermifyPermissionErrors[keyof VellumApiCheckPermifyPermissionErrors];

export type VellumApiCheckPermifyPermissionResponses = {
    /**
     * Successful Response
     */
    200: CheckPermissionResponseBody;
};

export type VellumApiCheckPermifyPermissionResponse = VellumApiCheckPermifyPermissionResponses[keyof VellumApiCheckPermifyPermissionResponses];

export type VellumApiGrantPermifyPermissionData = {
    body: GrantPermissionRequest;
    path: {
        /**
         * Tenant Id
         */
        tenant_id: string;
    };
    query?: never;
    url: '/api/v1/permify/tenants/{tenant_id}/permissions/grant';
};

export type VellumApiGrantPermifyPermissionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type VellumApiGrantPermifyPermissionError = VellumApiGrantPermifyPermissionErrors[keyof VellumApiGrantPermifyPermissionErrors];

export type VellumApiGrantPermifyPermissionResponses = {
    /**
     * Response Vellumapigrantpermifypermission
     *
     * Successful Response
     */
    200: {
        [key: string]: unknown;
    };
};

export type VellumApiGrantPermifyPermissionResponse = VellumApiGrantPermifyPermissionResponses[keyof VellumApiGrantPermifyPermissionResponses];

export type VellumApiListPermifyLunaUsersData = {
    body?: never;
    path: {
        /**
         * Tenant Id
         */
        tenant_id: string;
    };
    query?: never;
    url: '/api/v1/permify/tenants/{tenant_id}/luna/users';
};

export type VellumApiListPermifyLunaUsersErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type VellumApiListPermifyLunaUsersError = VellumApiListPermifyLunaUsersErrors[keyof VellumApiListPermifyLunaUsersErrors];

export type VellumApiListPermifyLunaUsersResponses = {
    /**
     * Response Vellumapilistpermifylunausers
     *
     * Successful Response
     */
    200: Array<AppUserPermifyPermissions>;
};

export type VellumApiListPermifyLunaUsersResponse = VellumApiListPermifyLunaUsersResponses[keyof VellumApiListPermifyLunaUsersResponses];

export type VellumApiListPermifyCustomerPlatformUsersData = {
    body?: never;
    path: {
        /**
         * Tenant Id
         */
        tenant_id: string;
    };
    query?: never;
    url: '/api/v1/permify/tenants/{tenant_id}/customer-platform/users';
};

export type VellumApiListPermifyCustomerPlatformUsersErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type VellumApiListPermifyCustomerPlatformUsersError = VellumApiListPermifyCustomerPlatformUsersErrors[keyof VellumApiListPermifyCustomerPlatformUsersErrors];

export type VellumApiListPermifyCustomerPlatformUsersResponses = {
    /**
     * Response Vellumapilistpermifycustomerplatformusers
     *
     * Successful Response
     */
    200: Array<AppUserPermifyPermissions>;
};

export type VellumApiListPermifyCustomerPlatformUsersResponse = VellumApiListPermifyCustomerPlatformUsersResponses[keyof VellumApiListPermifyCustomerPlatformUsersResponses];

export type VellumApiSyncPermifyLunaUserData = {
    body?: never;
    path: {
        /**
         * Tenant Id
         */
        tenant_id: string;
        /**
         * User Id
         */
        user_id: string;
    };
    query?: never;
    url: '/api/v1/permify/tenants/{tenant_id}/luna/users/{user_id}/sync';
};

export type VellumApiSyncPermifyLunaUserErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type VellumApiSyncPermifyLunaUserError = VellumApiSyncPermifyLunaUserErrors[keyof VellumApiSyncPermifyLunaUserErrors];

export type VellumApiSyncPermifyLunaUserResponses = {
    /**
     * Successful Response
     */
    200: SyncUserPermifyResponse;
};

export type VellumApiSyncPermifyLunaUserResponse = VellumApiSyncPermifyLunaUserResponses[keyof VellumApiSyncPermifyLunaUserResponses];

export type VellumApiSyncPermifyCustomerPlatformUserData = {
    body?: never;
    path: {
        /**
         * Tenant Id
         */
        tenant_id: string;
        /**
         * User Id
         */
        user_id: string;
    };
    query?: never;
    url: '/api/v1/permify/tenants/{tenant_id}/customer-platform/users/{user_id}/sync';
};

export type VellumApiSyncPermifyCustomerPlatformUserErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type VellumApiSyncPermifyCustomerPlatformUserError = VellumApiSyncPermifyCustomerPlatformUserErrors[keyof VellumApiSyncPermifyCustomerPlatformUserErrors];

export type VellumApiSyncPermifyCustomerPlatformUserResponses = {
    /**
     * Successful Response
     */
    200: SyncUserPermifyResponse;
};

export type VellumApiSyncPermifyCustomerPlatformUserResponse = VellumApiSyncPermifyCustomerPlatformUserResponses[keyof VellumApiSyncPermifyCustomerPlatformUserResponses];

export type ListCompaniesData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Company Id
         *
         * Filter by company ID
         */
        company_id?: string | null;
    };
    url: '/api/v1/companies';
};

export type ListCompaniesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ListCompaniesError = ListCompaniesErrors[keyof ListCompaniesErrors];

export type ListCompaniesResponses = {
    /**
     * Response Listcompanies
     *
     * Successful Response
     */
    200: Array<InternalCompanySchema>;
};

export type ListCompaniesResponse = ListCompaniesResponses[keyof ListCompaniesResponses];

export type ListFacilitiesData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Company Id
         *
         * Filter by company ID
         */
        company_id?: string | null;
    };
    url: '/api/v1/facilities';
};

export type ListFacilitiesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ListFacilitiesError = ListFacilitiesErrors[keyof ListFacilitiesErrors];

export type ListFacilitiesResponses = {
    /**
     * Response Listfacilities
     *
     * Successful Response
     */
    200: Array<InternalFacilitySchema>;
};

export type ListFacilitiesResponse = ListFacilitiesResponses[keyof ListFacilitiesResponses];

export type CreateMetabaseGuestTokenData = {
    body: MetabaseGuestTokenRequest;
    path?: never;
    query?: never;
    url: '/api/v1/metabase/guest-token';
};

export type CreateMetabaseGuestTokenErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type CreateMetabaseGuestTokenError = CreateMetabaseGuestTokenErrors[keyof CreateMetabaseGuestTokenErrors];

export type CreateMetabaseGuestTokenResponses = {
    /**
     * Successful Response
     */
    200: MetabaseGuestTokenResponse;
};

export type CreateMetabaseGuestTokenResponse = CreateMetabaseGuestTokenResponses[keyof CreateMetabaseGuestTokenResponses];

export type ListReportsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/reports';
};

export type ListReportsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ListReportsError = ListReportsErrors[keyof ListReportsErrors];

export type ListReportsResponses = {
    /**
     * Response Listreports
     *
     * Successful Response
     */
    200: Array<ReportSchema>;
};

export type ListReportsResponse = ListReportsResponses[keyof ListReportsResponses];

export type CreateReportData = {
    body: ReportCreateRequest;
    path?: never;
    query?: never;
    url: '/api/v1/reports';
};

export type CreateReportErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type CreateReportError = CreateReportErrors[keyof CreateReportErrors];

export type CreateReportResponses = {
    /**
     * Successful Response
     */
    201: ReportSchema;
};

export type CreateReportResponse = CreateReportResponses[keyof CreateReportResponses];

export type DeleteReportData = {
    body?: never;
    path: {
        /**
         * Report Id
         */
        report_id: string;
    };
    query?: never;
    url: '/api/v1/reports/{report_id}';
};

export type DeleteReportErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteReportError = DeleteReportErrors[keyof DeleteReportErrors];

export type DeleteReportResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteReportResponse = DeleteReportResponses[keyof DeleteReportResponses];

export type UpdateReportData = {
    body: ReportUpdateRequest;
    path: {
        /**
         * Report Id
         */
        report_id: string;
    };
    query?: never;
    url: '/api/v1/reports/{report_id}';
};

export type UpdateReportErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UpdateReportError = UpdateReportErrors[keyof UpdateReportErrors];

export type UpdateReportResponses = {
    /**
     * Successful Response
     */
    200: ReportSchema;
};

export type UpdateReportResponse = UpdateReportResponses[keyof UpdateReportResponses];

export type GetReportData = {
    body?: never;
    path: {
        /**
         * Slug
         */
        slug: string;
    };
    query?: never;
    url: '/api/v1/reports/{slug}';
};

export type GetReportErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetReportError = GetReportErrors[keyof GetReportErrors];

export type GetReportResponses = {
    /**
     * Successful Response
     */
    200: ReportSchema;
};

export type GetReportResponse = GetReportResponses[keyof GetReportResponses];

export type ReorderReportsData = {
    body: ReportsReorderRequest;
    path?: never;
    query?: never;
    url: '/api/v1/reports/order';
};

export type ReorderReportsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ReorderReportsError = ReorderReportsErrors[keyof ReorderReportsErrors];

export type ReorderReportsResponses = {
    /**
     * Response Reorderreports
     *
     * Successful Response
     */
    200: Array<ReportSchema>;
};

export type ReorderReportsResponse = ReorderReportsResponses[keyof ReorderReportsResponses];

export type ListSubscriptionAutoRecipientsData = {
    body?: never;
    path?: never;
    query: {
        type: SubscriptionType;
        /**
         * Company Id
         */
        company_id: string;
        /**
         * Facility Id
         */
        facility_id?: string | null;
    };
    url: '/api/v1/subscriptions/auto-recipients';
};

export type ListSubscriptionAutoRecipientsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ListSubscriptionAutoRecipientsError = ListSubscriptionAutoRecipientsErrors[keyof ListSubscriptionAutoRecipientsErrors];

export type ListSubscriptionAutoRecipientsResponses = {
    /**
     * Response Listsubscriptionautorecipients
     *
     * Successful Response
     */
    200: Array<SubscriptionAutoRecipientSchema>;
};

export type ListSubscriptionAutoRecipientsResponse = ListSubscriptionAutoRecipientsResponses[keyof ListSubscriptionAutoRecipientsResponses];

export type ListSubscriptionContactsData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Company Id
         */
        company_id: string;
        /**
         * Facility Id
         */
        facility_id?: string | null;
    };
    url: '/api/v1/subscriptions/contacts';
};

export type ListSubscriptionContactsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ListSubscriptionContactsError = ListSubscriptionContactsErrors[keyof ListSubscriptionContactsErrors];

export type ListSubscriptionContactsResponses = {
    /**
     * Response Listsubscriptioncontacts
     *
     * Successful Response
     */
    200: Array<SubscriptionContactSchema>;
};

export type ListSubscriptionContactsResponse = ListSubscriptionContactsResponses[keyof ListSubscriptionContactsResponses];

export type ListSubscriptionsData = {
    body?: never;
    path?: never;
    query: {
        typ: SubscriptionType;
        /**
         * Company Id
         */
        company_id: string;
        /**
         * Facility Id
         */
        facility_id?: string | null;
    };
    url: '/api/v1/subscriptions';
};

export type ListSubscriptionsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ListSubscriptionsError = ListSubscriptionsErrors[keyof ListSubscriptionsErrors];

export type ListSubscriptionsResponses = {
    /**
     * Response Listsubscriptions
     *
     * Successful Response
     */
    200: Array<DashboardSubscriptionSchema>;
};

export type ListSubscriptionsResponse = ListSubscriptionsResponses[keyof ListSubscriptionsResponses];

export type CreateSubscriptionData = {
    body: SubscriptionCreateRequest;
    path?: never;
    query?: never;
    url: '/api/v1/subscriptions';
};

export type CreateSubscriptionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type CreateSubscriptionError = CreateSubscriptionErrors[keyof CreateSubscriptionErrors];

export type CreateSubscriptionResponses = {
    /**
     * Successful Response
     */
    201: DashboardSubscriptionSchema;
};

export type CreateSubscriptionResponse = CreateSubscriptionResponses[keyof CreateSubscriptionResponses];

export type DeleteSubscriptionData = {
    body?: never;
    path: {
        /**
         * Subscription Id
         */
        subscription_id: string;
    };
    query?: never;
    url: '/api/v1/subscriptions/{subscription_id}';
};

export type DeleteSubscriptionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteSubscriptionError = DeleteSubscriptionErrors[keyof DeleteSubscriptionErrors];

export type DeleteSubscriptionResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteSubscriptionResponse = DeleteSubscriptionResponses[keyof DeleteSubscriptionResponses];

export type UpdateSubscriptionData = {
    body: SubscriptionUpdateRequest;
    path: {
        /**
         * Subscription Id
         */
        subscription_id: string;
    };
    query?: never;
    url: '/api/v1/subscriptions/{subscription_id}';
};

export type UpdateSubscriptionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UpdateSubscriptionError = UpdateSubscriptionErrors[keyof UpdateSubscriptionErrors];

export type UpdateSubscriptionResponses = {
    /**
     * Successful Response
     */
    200: DashboardSubscriptionSchema;
};

export type UpdateSubscriptionResponse = UpdateSubscriptionResponses[keyof UpdateSubscriptionResponses];

export type GetSubscriptionHistoryData = {
    body?: never;
    path: {
        /**
         * Subscription Id
         */
        subscription_id: string;
    };
    query?: never;
    url: '/api/v1/subscriptions/{subscription_id}/history';
};

export type GetSubscriptionHistoryErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetSubscriptionHistoryError = GetSubscriptionHistoryErrors[keyof GetSubscriptionHistoryErrors];

export type GetSubscriptionHistoryResponses = {
    /**
     * Response Getsubscriptionhistory
     *
     * Successful Response
     */
    200: Array<SubscriptionSendHistorySchema>;
};

export type GetSubscriptionHistoryResponse = GetSubscriptionHistoryResponses[keyof GetSubscriptionHistoryResponses];

export type SendSubscriptionData = {
    body: SendSubscriptionRequest;
    path: {
        /**
         * Subscription Id
         */
        subscription_id: string;
    };
    query?: never;
    url: '/api/v1/subscriptions/{subscription_id}/send';
};

export type SendSubscriptionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type SendSubscriptionError = SendSubscriptionErrors[keyof SendSubscriptionErrors];

export type SendSubscriptionResponses = {
    /**
     * Response Sendsubscription
     *
     * Successful Response
     */
    201: Array<SubscriptionSendHistorySchema>;
};

export type SendSubscriptionResponse = SendSubscriptionResponses[keyof SendSubscriptionResponses];
